import React from 'react';
import { useTranslation } from 'react-i18next';
import { AssetIssueReportSummary } from '../../../api/asset/assetApiTypes';
import AssetDetailSection from './AssetDetailSection';
import { STATION_TIME_ZONE } from '../../../utils/stationTime';

interface OpenIssueReportsSectionProps {
    reports: AssetIssueReportSummary[];
    mode: 'view' | 'edit' | 'create';
    /** #3219: the zone of the object's location; the station default when omitted. */
    timeZone?: string;
}

const stateColors: Record<string, string> = {
    open: 'bg-red-100 text-red-800',
    in_progress: 'bg-amber-100 text-amber-800',
    on_hold: 'bg-gray-100 text-gray-800',
};

const OpenIssueReportsSection: React.FC<OpenIssueReportsSectionProps> = ({ reports, timeZone = STATION_TIME_ZONE }) => {
    const { t, i18n } = useTranslation();
    const dateLocale = i18n.language.startsWith('de') ? 'de-DE' : 'en-US';

    const formatDate = (dateStr: string): string => {
        try {
            return new Date(dateStr).toLocaleDateString(dateLocale, {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                timeZone,
            });
        } catch {
            return dateStr;
        }
    };

    const hasReports = reports && reports.length > 0;

    return (
        <AssetDetailSection title={t('asset_detail.sections.open_issue_reports')}>
            {!hasReports ? (
                <p className={'text-sm text-foreground/40 italic'}>
                    {t('asset_detail.empty_states.open_issue_reports')}
                </p>
            ) : (
                <div className={'flex flex-col gap-3'}>
                    {reports.map((report) => (
                        <div
                            key={report.id}
                            className={'flex items-start gap-3 p-3 rounded-lg bg-foreground/[0.02] border border-foreground/5'}
                        >
                            <span
                                className={`shrink-0 mt-0.5 px-2 py-0.5 rounded-full text-xs font-medium ${
                                    stateColors[report.state] ?? 'bg-gray-100 text-gray-800'
                                }`}
                            >
                                {t(`asset_detail.issue_state.${report.state}`)}
                            </span>
                            <div className={'flex-1 min-w-0'}>
                                <p className={'text-sm text-foreground truncate'}>
                                    {report.message || t('asset_detail.fields.no_description')}
                                </p>
                                <div className={'flex gap-3 mt-1 text-xs text-foreground/50'}>
                                    <span>{formatDate(report.createdAt)}</span>
                                    {report.issueType && <span>{report.issueType}</span>}
                                    {report.bookingNumber && <span>{report.bookingNumber}</span>}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </AssetDetailSection>
    );
};

export default OpenIssueReportsSection;
